import { useEffect } from 'react'
import { BrowserRouter, StaticRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'

import Header from './components/common/header/Header'
import Hero from './components/hero/Hero'
import Mission from './components/mission/Mission'
import Team from './components/team/Team'
import Roadmap from './components/roadmap/Roadmap'
import Token from './components/token/Token'
import Contact from './components/contact/Contact'
import TermsAndConditions from './components/terms-and-conditions/TermsAndConditions'
import Footer from './components/common/footer/Footer'
import NotFound from './components/not-found/NotFound'
import SEO from './components/seo/SEO'
import LatestPosts from './components/latest-posts/LatestPosts'
import About from './components/info-pages/About'
import HowItWorks from './components/info-pages/HowItWorks'
import Faq from './components/info-pages/Faq'

import { LanguageContext } from './contexts/LanguageContext'
import { useLanguage } from './hooks/useLanguage'
import './styles/App.css'
import Solutions from './components/solutions/Solutions'

import ReactGA from 'react-ga4';

// Initialize Google Analytics once. This module is also loaded in Node to
// pre-render pages at build time, where there is no browser to track.
const MEASUREMENT_ID = "G-T133G1R3Z6";
if (typeof window !== 'undefined') {
  ReactGA.initialize(MEASUREMENT_ID);
}

// Inner component to handle location tracking
function AppContent() {
  const location = useLocation();

  useEffect(() => {
    // Track page views
    ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
  }, [location])

  // Client-side navigation keeps the old scroll position, so a link at the foot of one
  // page would open the next one at its foot too. Links to a section keep their jump.
  // 'instant' overrides the site-wide smooth scrolling, which would animate up the old page.
  useEffect(() => {
    if (!location.hash) window.scrollTo({ top: 0, behavior: 'instant' })
  }, [location.pathname, location.hash])

  useEffect(() => {
    const handleSmoothScroll = (e: any) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault()
        const id = target.getAttribute('href')?.slice(1)
        if (id) {
          const element = document.getElementById(id)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
          }
        }
      }
    }

    document.addEventListener('click', handleSmoothScroll)
    return () => document.removeEventListener('click', handleSmoothScroll)
  }, [])

  return (
    <>
      <Header />

      <main>
        <Routes>

          {/* Redirect / to /home */}
          <Route path="/" element={<Navigate to="/home" replace />} />

          {/* HOME PAGE — show all except TermsAndConditions */}
          <Route
            path="/home"
            element={
              <>
                <Hero />
                <Mission />
                <Solutions />
                <Roadmap />
                <Token />
                <Team />
                <LatestPosts />
                <Contact />
                {/* Each section sets its own SEO tags and the last one rendered wins;
                    this keeps the page's title and description the site's own. */}
                <SEO />
              </>
            }
          />

          {/* TERMS PAGE — show only TermsAndConditions */}
          <Route path="/terms" element={<TermsAndConditions />} />

          {/* The privacy policy and terms are one document */}
          <Route
            path="/privacy"
            element={
              <>
                <TermsAndConditions />
                <SEO
                  title="MakeMyDay Privacy Policy | How We Handle Your Data"
                  description="How MakeMyDay collects, uses, stores and protects personal data in the mobile and web app, including advertising, cookies and your GDPR rights."
                />
              </>
            }
          />

          <Route path="/about" element={<About />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/faq" element={<Faq />} />

          <Route path="*" element={<NotFound />} />

        </Routes>
      </main>

      <Footer />
    </>
  )
}

// `location` is set only when pre-rendering a route at build time.
function App({ location }: { location?: string }) {
  const { language, setLanguage, content } = useLanguage()

  return (
    <LanguageContext.Provider value={{ language, setLanguage, content }}>
      {location ? (
        <StaticRouter location={location}>
          <AppContent />
        </StaticRouter>
      ) : (
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      )}
    </LanguageContext.Provider>
  )
}

export default App
